import { GridRowId } from '@mui/x-data-grid-pro';
import { Device } from '@src/types/Device';
import { sortAlphaNum } from '@utils/sortAlphaNum';
import { useCallback, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY_PREFIX = 'expandedRows_';

function getStoredExpandedIds(
  storageKey: string | undefined,
): GridRowId[] | null {
  if (!storageKey) {
    return null;
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${storageKey}`);
    if (stored) {
      return JSON.parse(stored) as GridRowId[];
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
}

function saveExpandedIds(
  storageKey: string | undefined,
  ids: GridRowId[],
): void {
  if (!storageKey) {
    return;
  }
  try {
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${storageKey}`,
      JSON.stringify(ids),
    );
  } catch {
    // Ignore localStorage errors
  }
}

export default function useExpandedRows(
  devices: Device[] | undefined,
  maxInitialExpanded = 5,
  storageKey?: string,
): {
  expandedRowIds: GridRowId[];
  setExpandedRowIds: React.Dispatch<React.SetStateAction<GridRowId[]>>;
} {
  const initialExpandedIds = useMemo(() => {
    if (!devices || devices.length === 0) {
      return [];
    }

    return sortAlphaNum(devices, 'name')
      .slice(0, maxInitialExpanded)
      .map((device) => device.engineeringId);
  }, [devices, maxInitialExpanded]);

  const [expandedRowIds, setExpandedRowIds] = useState<GridRowId[]>(() => {
    const stored = getStoredExpandedIds(storageKey);
    return stored ?? initialExpandedIds;
  });

  const [hasInitialized, setHasInitialized] = useState(false);

  // On initial load, check localStorage or use defaults
  useEffect(() => {
    if (hasInitialized || !devices || devices.length === 0) {
      return;
    }

    const stored = getStoredExpandedIds(storageKey);
    if (stored !== null) {
      // Filter to only include IDs that exist in current devices
      const validIds = devices.map((d) => d.engineeringId);
      const filteredStored = stored.filter((id) =>
        validIds.includes(id as string),
      );
      setExpandedRowIds(filteredStored);
    } else {
      setExpandedRowIds(initialExpandedIds);
    }
    setHasInitialized(true);
  }, [devices, initialExpandedIds, storageKey, hasInitialized]);

  // Save to localStorage whenever expandedRowIds changes
  useEffect(() => {
    if (hasInitialized) {
      saveExpandedIds(storageKey, expandedRowIds);
    }
  }, [expandedRowIds, storageKey, hasInitialized]);

  const setExpandedRowIdsWithStorage = useCallback<
    React.Dispatch<React.SetStateAction<GridRowId[]>>
  >((action) => {
    setExpandedRowIds(action);
  }, []);

  return { expandedRowIds, setExpandedRowIds: setExpandedRowIdsWithStorage };
}
